import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const IncomeRecordTable = ({ printMode = false }) => {
  const { list, details, listWithDetails, filters, loading } = useSelector(
    (state) => state.incomeRecord
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  let dataSource = [];
  if (filters.filterType === "By Income") {
    dataSource = details;
  } else if (filters.recordType === "with details") {
    dataSource = listWithDetails;
  } else {
    dataSource = list;
  }

  let columns = [];
  if (filters.filterType === "By Income") {
    columns = [
      { title: "No.", render: (_, __, index) => index + 1, width: 60 },
      { title: "VCH No", dataIndex: "inc_code" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (date) => dayjs(date).format("YYYY-MMM-DD"),
      },
      { title: "Income From", dataIndex: "acc_name" },
      { title: "Receipt By", dataIndex: "full_name" },
      { title: "Amount", dataIndex: "inc_amount", align: "right", render: fmt },
    ];
  } else if (filters.recordType === "with details") {
    columns = [
      { title: "No.", render: (_, __, index) => index + 1 },
      { title: "VCH", dataIndex: "inc_code" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (date) => dayjs(date).format("YYYY-MMM-DD"),
      },
      { title: "Receipt Into", dataIndex: "acc_name" },
      { title: "Receipt By", dataIndex: "full_name" },
      {
        title: "Details",
        dataIndex: "details",
        render: (details) => (
          <>
            {details?.map((d, i) => (
              <div
                key={i}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span>{d.acc_name}</span>
                <span>{fmt(d.inc_amount)}</span>
              </div>
            ))}
            <strong>Sub Total: {fmt(_.sumBy(details, "inc_amount"))}</strong>
          </>
        ),
      },
    ];
  } else {
    columns = [
      { title: "No.", render: (_, __, index) => index + 1 },
      { title: "VCH No", dataIndex: "inc_code" },
      {
        title: "Date",
        dataIndex: "created_date",
        render: (date) => dayjs(date).format("YYYY-MMM-DD"),
      },
      { title: "Receipt Into", dataIndex: "acc_name" },
      { title: "Receipt By", dataIndex: "full_name" },
      {
        title: "Total Amount",
        dataIndex: "inc_total",
        align: "right",
        render: fmt,
      },
    ];
  }

  const grandTotal =
    filters.filterType === "By Income"
      ? _.sumBy(details, (r) => Number(r.inc_amount) || 0)
      : filters.recordType === "with details"
      ? _.sumBy(listWithDetails, (r) => Number(r.inc_total) || 0)
      : _.sumBy(list, (r) => Number(r.inc_total) || 0);

  if (printMode) {

  // By Income
  if (filters.filterType === "By Income") {
    return (
      <table className="income-print-table">
        <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "30%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "15%" }} />
        </colgroup>

        <tbody>
          <tr className="income-print-heading">
            <td>No.</td>
            <td>VCH No</td>
            <td>Date</td>
            <td>Income From</td>
            <td>Receipt By</td>
            <td style={{ textAlign: "right" }}>Amount</td>
          </tr>

          {details.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>
              <td>{row.inc_code}</td>
              <td>{dayjs(row.created_date).format("YYYY-MMM-DD")}</td>
              <td>{row.acc_name}</td>
              <td>{row.full_name}</td>
              <td style={{ textAlign: "right" }}>
                {fmt(row.inc_amount)}
              </td>
            </tr>
          ))}

          <tr className="income-print-total">
            <td colSpan={5} style={{ textAlign: "right" }}>
              Grand Total :
            </td>
            <td style={{ textAlign: "right" }}>
              {fmt(grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  // With Details
  if (filters.recordType === "with details") {
    return (
      <table className="income-print-table">
        <colgroup>
          <col style={{ width: "5%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "38%" }} />
        </colgroup>

        <tbody>
          <tr className="income-print-heading">
            <td>No.</td>
            <td>VCH</td>
            <td>Date</td>
            <td>Receipt Into</td>
            <td>Receipt By</td>
            <td>Details</td>
          </tr>

          {listWithDetails.map((row, index) => (
            <tr key={row.id || index}>
              <td>{index + 1}</td>
              <td>{row.inc_code}</td>
              <td>{dayjs(row.created_date).format("YYYY-MMM-DD")}</td>
              <td>{row.acc_name}</td>
              <td>{row.full_name}</td>

              <td>
                {row.details?.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{d.acc_name}</span>
                    <span>{fmt(d.inc_amount)}</span>
                  </div>
                ))}

                <div style={{ fontWeight: 700 }}>
                  Sub Total : {fmt(_.sumBy(row.details, "inc_amount"))}
                </div>
              </td>
            </tr>
          ))}

          <tr className="income-print-total">
            <td colSpan={5} style={{ textAlign: "right" }}>
              Grand Total :
            </td>
            <td style={{ textAlign: "right" }}>
              {fmt(grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  // Default
  return (
    <table className="income-print-table">
      <colgroup>
        <col style={{ width: "6%" }} />
        <col style={{ width: "16%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "34%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "15%" }} />
      </colgroup>

      <tbody>
        <tr className="income-print-heading">
          <td>No.</td>
          <td>VCH No</td>
          <td>Date</td>
          <td>Receipt Into</td>
          <td>Receipt By</td>
          <td style={{ textAlign: "right" }}>Total Amount</td>
        </tr>

        {list.map((row, index) => (
          <tr key={row.id || index}>
            <td>{index + 1}</td>
            <td>{row.inc_code}</td>
            <td>{dayjs(row.created_date).format("YYYY-MMM-DD")}</td>
            <td>{row.acc_name}</td>
            <td>{row.full_name}</td>

            <td style={{ textAlign: "right" }}>
              {fmt(row.inc_total)}
            </td>
          </tr>
        ))}

        <tr className="income-print-total">
          <td colSpan={5} style={{ textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={{ textAlign: "right" }}>
            {fmt(grandTotal)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey={(record, idx) => record.id || idx}
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      bordered
      size={printMode ? "small" : "middle"}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell
            index={0}
            colSpan={columns.length - 1}
            align="right"
          >
            <strong>Grand Total :</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={columns.length} align="right">
            <strong>{fmt(grandTotal)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default IncomeRecordTable;
